import {
    Box,
    Button,
    Checkbox,
    Divider,
    FormControl,
    FormControlLabel,
    MenuItem,
    Select,
    Skeleton,
    Stack,
    TextField,
    Typography,
} from "@mui/material";
import Switch from "@mui/material/Switch";
import { DatePicker } from "@mui/x-date-pickers/DatePicker";
import React, { useEffect, useState } from "react";
import { useSelector } from "react-redux";
import { toast } from "react-toastify";
import {
    commandReward,
    runReward,
    updateRewardSettings,
} from "../api/bot.api";
import { USDC_MINT, USDT_MINT } from "../libs/constants";
import {
    displayWithNonZeroDecimals,
    getTokenBalance,
    shortenAddress,
} from "../libs/utils";
import { RootState } from "../store";
import CopyButton from "./CopyButton";
import {
    AmountOption,
    IntervalOption,
    IReward2Settings,
    RewardStatus,
} from "./types";
import { LocalizationProvider } from "@mui/x-date-pickers";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import dayjs from "dayjs";
import NumberField from "./NumberField";

interface RewardSettingsProps {
    getRewardSettingsHandler: () => void;
}

export default function RewardSettings({getRewardSettingsHandler}: RewardSettingsProps) {
    const rewardSettingsState = useSelector((state: RootState) => state.rewardSettings);
    const [rewardSettings, setRewardSettings] = useState<IReward2Settings>(rewardSettingsState);
    const [isRewarding, setIsRewarding] = useState(false);
    const [isUpdatingRewardSettings, setIsUpdatingRewardSettings] = useState(false);
    const [maxRewardAmount, setMaxRewardAmount] = useState(0);
    const [isCommanding, setIsCommanding] = useState(false);

    useEffect(() => {
        if (rewardSettings._id) {
            setRewardSettings({
                ...rewardSettings,
                rewardVersion: rewardSettingsState.rewardVersion,
                status: rewardSettingsState.status,
                msg: rewardSettingsState.msg,
            });
        } else {
            setRewardSettings({
                ...rewardSettingsState,
            });
        }
    }, [rewardSettingsState]);

    useEffect(() => {
        const getMaxRewardAmount = async () => {
            const balance = await getTokenBalance(
                rewardSettings.walletPubkey,
                rewardSettings.rewardToken
            );
            setMaxRewardAmount(balance);
        };
        getMaxRewardAmount();
    }, [rewardSettings.walletPubkey, rewardSettings.rewardToken]);

    const rewardHandler = async () => {
        setIsRewarding(true);
        const response = await runReward({});
        if (response.success) {
            toast.success(response.msg);
            getRewardSettingsHandler();
        } else {
            toast.error(response.msg);
        }
        setIsRewarding(false);
    };

    const updateRewardSettingsHandler = async () => {
        const msg = checkValidRewardSettings();
        if (msg) {
            toast.error(msg);
            return;
        }
        setIsUpdatingRewardSettings(true);
        console.log(rewardSettings);
        const response = await updateRewardSettings({ rewardSettings });
        if (response.success) {
            toast.success(response.msg);
        } else {
            toast.error(response.msg);
        }
        setIsUpdatingRewardSettings(false);
    };

    const checkValidRewardSettings = () => {
        let msg = "";
        if (!rewardSettings.rewardToken) {
            msg = "Reward token must be selected";
            return msg;
        }
        if (
            (rewardSettings.amountOption === AmountOption.Static ||
                rewardSettings.amountOption === AmountOption.StaticAuto) &&
            Number(rewardSettings.staticAmount) <= 0
        ) {
            msg = "Amount in 'Amount Option' must be greater than 0";
            return msg;
        }
        if (rewardSettings.isEnableUs) {
            if (!rewardSettings.percent2Us || Number(rewardSettings.percent2Us) <= 0) {
                msg = "Percent to You must be greater than 0";
                return msg;
            } else if (!rewardSettings.usAddress) {
                msg = "Your wallet address is required";
                return msg;
            }
        }
        if (
            rewardSettings.intervalOption !== IntervalOption.Manual &&
            !rewardSettings.lastRewardDate
        ) {
            msg = "Last reward date is required";
            return msg;
        }
        return "";
    };

    const commandRewardHandler = async (command: string) => {
        setIsCommanding(true);
        const response = await commandReward({ command });
        if (response.success) {
            toast.success(response.msg);
            getRewardSettingsHandler();
        } else {
            toast.error(response.msg);
        }
        setIsCommanding(false);
    };

    return (
        <Box display="flex" flexDirection="column" alignItems="center"
            sx={{ borderRight: {xs: "none", md: '1px solid #1e1e1e'}, overflowY:'auto', pt: 0, pb: 0}}>
            <Box
                display="flex"
                flexDirection="column"
                alignItems="center"
                gap={2}
                pb={4}
                sx={{
                    width: {md: 320, xs: '100%'},
                    maxWidth: {md: 320, xs: '450px'},
                    minWidth: {md: 320},
                    borderRight: {md: "1px solid #1e1e1e", xs: "none"},
                    px: {md:2, sm: 6, xs: 2},
                    pt: 3,
                    overflowY: "auto",
                    border: {md: "none", xs: '1px solid #1e1e1e'}
                }}
            >
                <Box
                    display="flex"
                    flexDirection="row"
                    alignItems="center"
                    justifyContent="space-between"
                    gap={2}
                    sx={{ width: "100%" }}
                >
                    <Button
                        variant="outlined"
                        color="primary"
                        sx={{ width: 80 }}
                        onClick={updateRewardSettingsHandler}
                        loading={isUpdatingRewardSettings}
                        loadingIndicator="Updating"
                    >
                        Update
                    </Button>
                    <Typography variant="h6" color="text.secondary">
                        Reward2 Settings
                    </Typography>
                </Box>
                <Box
                    display="flex"
                    alignItems="center"
                    justifyContent="space-between"
                    gap={2}
                    sx={{ width: "100%" }}
                >
                    <Typography variant="subtitle1">Next Reward Version</Typography>
                    <Typography variant="h6">
                        {rewardSettings.rewardVersion + 1}
                    </Typography>
                </Box>

                <Box
                    display="flex"
                    alignItems="center"
                    justifyContent="space-between"
                    gap={2}
                    sx={{ width: "100%" }}
                >
                    <Typography variant="subtitle1">
                        System is {rewardSettings.isEnable ? "Enabled" : "Disabled"}
                    </Typography>
                    <Switch
                        checked={rewardSettings.isEnable}
                        onChange={(e) =>
                            setRewardSettings({
                                ...rewardSettings,
                                isEnable: e.target.checked,
                            })
                        }
                    />
                </Box>

                <Box
                    display="flex"
                    alignItems="center"
                    justifyContent="space-between"
                    gap={2}
                    sx={{ width: "100%" }}
                >
                    <Typography variant="subtitle1">Sender Wallet</Typography>
                    {rewardSettings.walletPubkey ? (
                        <Box display="flex" alignItems="center" sx={{}}>
                            <Typography variant="subtitle2">
                                {shortenAddress(rewardSettings?.walletPubkey, 4)}
                            </Typography>
                            <CopyButton text={rewardSettings?.walletPubkey} />
                        </Box>
                    ) : (
                        <Skeleton variant="rounded" width={50} height={30} />
                    )}
                </Box>

                <Box display="flex" flexDirection="column" sx={{ width: "100%" }}>
                    <Box
                        display="flex"
                        alignItems="center"
                        justifyContent="space-between"
                        gap={2}
                        sx={{ width: "100%" }}
                    >
                        <Typography variant="subtitle1">Reward Token</Typography>
                        <FormControl
                            variant="outlined"
                            size="small"
                            sx={{ width: 120 }}
                        >
                            <Select
                                value={rewardSettings.rewardToken}
                                onChange={(e) =>
                                    setRewardSettings({
                                        ...rewardSettings,
                                        rewardToken: e.target.value,
                                    })
                                }
                                displayEmpty
                                inputProps={{ "aria-label": "Select Coin" }}
                            >
                                <MenuItem value={USDC_MINT}>USDC</MenuItem>
                                <MenuItem value={USDT_MINT}>USDT</MenuItem>
                            </Select>
                        </FormControl>
                    </Box>
                    <Box
                        display="flex"
                        alignItems="center"
                        justifyContent="flex-end"
                        sx={{ width: "100%", mt: 0.5 }}
                    >
                        <Typography variant="subtitle1" fontSize={12} color="divider">
                            Sender Balance:{" "}
                            {displayWithNonZeroDecimals(maxRewardAmount, 1)}
                        </Typography>
                    </Box>
                </Box>
                <Divider textAlign="left" sx={{ width: "100%" }}>
                    Amount Option
                </Divider>
                <Box
                    display="flex"
                    flexDirection="column"
                    justifyContent="space-between"
                    gap={1}
                    sx={{ width: "100%" }}
                >
                    <Stack
                        direction="row"
                        alignItems="center"
                        justifyContent="space-between"
                        gap={2}
                        sx={{ width: "100%" }}
                    >
                        <Select
                            value={rewardSettings.amountOption}
                            onChange={(e) =>
                                setRewardSettings({
                                    ...rewardSettings,
                                    amountOption: e.target.value as AmountOption,
                                })
                            }
                            displayEmpty
                            inputProps={{ "aria-label": "Select Amount Option" }}
                            size="small"
                        >
                            <MenuItem value={AmountOption.AutoMax}>Auto Max</MenuItem>
                            <MenuItem value={AmountOption.Static}>Static</MenuItem>
                            <MenuItem value={AmountOption.StaticAuto}>
                                Static Auto
                            </MenuItem>
                        </Select>
                        {rewardSettings.amountOption !== AmountOption.AutoMax && (
                            <NumberField
                                label="Amount"
                                sx={{ width: 1 }}
                                value={rewardSettings.staticAmount.toString()}
                                onChange={(value) =>
                                    setRewardSettings({
                                        ...rewardSettings,
                                        staticAmount: value,
                                    })
                                }
                            />
                        )}
                    </Stack>
                    <Typography
                        variant="subtitle1"
                        fontSize={12}
                        color="text.secondary"
                    >
                        {rewardSettings.amountOption === AmountOption.AutoMax &&
                            "Automatically using sender's reward token balance for reward."}
                        {rewardSettings.amountOption === AmountOption.Static &&
                            "Using static amount for reward. If wallet balance is less than the static amount, reward will not be processed."}
                        {rewardSettings.amountOption === AmountOption.StaticAuto &&
                            "Using wallet balance with a maximum of the static amount for reward."}
                    </Typography>
                    <Box
                        display="flex"
                        alignItems="center"
                        justifyContent="space-between"
                        gap={2}
                        sx={{ width: "100%", mt: 0 }}
                    >
                        <NumberField
                            label="Min Amount"
                            sx={{ width: 1 }}
                            value={rewardSettings.minAmount}
                            onChange={(value) =>
                                setRewardSettings({
                                    ...rewardSettings,
                                    minAmount: value,
                                })
                            }
                        />
                        <Typography
                            variant="subtitle1"
                            fontSize={12}
                            color="text.secondary"
                        >
                            : Minimum SMRTY amount than can be rewarded.
                        </Typography>
                    </Box>
                </Box>
                <Divider textAlign="left" sx={{ width: "100%" }}>
                    Reward to You
                </Divider>

                <Box
                    display="flex"
                    alignItems="left"
                    flexDirection="column"
                    gap={2}
                    sx={{ width: "100%" }}
                >
                    <FormControlLabel
                        control={
                            <Checkbox
                                checked={rewardSettings.isEnableUs}
                                onChange={(e) =>
                                    setRewardSettings({
                                        ...rewardSettings,
                                        isEnableUs: e.target.checked,
                                    })
                                }
                            />
                        }
                        label="Enable % to You"
                    />
                    {rewardSettings.isEnableUs && (
                        <>
                            <NumberField
                                label="% to You"
                                placeholder="10"
                                sx={{ width: 1 }}
                                value={rewardSettings.percent2Us || ""}
                                onChange={(value) =>
                                    setRewardSettings({
                                        ...rewardSettings,
                                        percent2Us: value,
                                    })
                                }
                            />
                            <TextField
                                label="Your Wallet Address"
                                multiline
                                rows={3}
                                size="small"
                                variant="outlined"
                                placeholder=""
                                sx={{ width: 1 }}
                                value={rewardSettings.usAddress}
                                onChange={(e) =>
                                    setRewardSettings({
                                        ...rewardSettings,
                                        usAddress: e.target.value,
                                    })
                                }
                            />
                        </>
                    )}
                </Box>

                <Divider textAlign="left" sx={{ width: "100%" }}></Divider>

                <Box
                    display="flex"
                    alignItems="center"
                    flexDirection="column"
                    justifyContent="space-between"
                    gap={2}
                    sx={{ width: "100%" }}
                >
                    <Box
                        display="flex"
                        alignItems="center"
                        justifyContent="space-between"
                        sx={{ width: "100%" }}
                    >
                        <Typography variant="subtitle1">Interval Option</Typography>
                        <Select
                            value={rewardSettings.intervalOption}
                            onChange={(e) =>
                                setRewardSettings({
                                    ...rewardSettings,
                                    intervalOption: e.target.value as IntervalOption,
                                })
                            }
                            displayEmpty
                            inputProps={{ "aria-label": "Select Interval Option" }}
                            size="small"
                            sx={{ minWidth: 160 }}
                        >
                            <MenuItem value={IntervalOption.Manual}>Manual</MenuItem>
                            <MenuItem value={IntervalOption.Weekly}>Weekly</MenuItem>
                            <MenuItem value={IntervalOption.BiWeekly}>Bi-Weekly</MenuItem>
                            <MenuItem value={IntervalOption.Monthly}>Monthly</MenuItem>
                            <MenuItem value={IntervalOption.TwoMonthly}>
                                Two Monthly
                            </MenuItem>
                            <MenuItem value={IntervalOption.ThreeMonthly}>
                                Three Monthly
                            </MenuItem>
                        </Select>
                    </Box>
                    {rewardSettings.intervalOption !== IntervalOption.Manual && (
                        <LocalizationProvider dateAdapter={AdapterDayjs}>
                            <DatePicker
                                label="Last Reward Date"
                                value={
                                    rewardSettings.lastRewardDate
                                        ? dayjs(rewardSettings.lastRewardDate)
                                        : null
                                }
                                onChange={(newValue) =>
                                    setRewardSettings({
                                        ...rewardSettings,
                                        lastRewardDate: dayjs(newValue).toDate(),
                                    })
                                }
                            />
                        </LocalizationProvider>
                    )}
                </Box>

                {rewardSettings.intervalOption === IntervalOption.Manual &&
                    rewardSettings.status !== RewardStatus.Running &&
                    rewardSettings.status !== RewardStatus.Searching && (
                        <Box
                            display="flex"
                            flexDirection="column"
                            alignItems="center"
                            justifyContent="space-between"
                            gap={2}
                            sx={{ width: "100%" }}
                        >
                            <Button
                                variant="contained"
                                color="secondary"
                                sx={{ minWidth: 200 }}
                                onClick={rewardHandler}
                                loading={isRewarding}
                                loadingIndicator="Rewarding…"
                            >
                                Run Reward
                            </Button>
                        </Box>
                    )}
                <Divider textAlign="left" sx={{ width: "100%" }}></Divider>
                <Box
                    display="flex"
                    flexDirection="column"
                    alignItems="center"
                    justifyContent="space-between"
                    gap={2}
                    sx={{ width: "100%" }}
                >
                    {rewardSettings.status === RewardStatus.Running && (
                        <Button
                            variant="contained"
                            color="primary"
                            sx={{ minWidth: 200 }}
                            onClick={() => commandRewardHandler("stop")}
                            loading={isCommanding}
                            loadingIndicator="Stopping…"
                        >
                            Stop Reward
                        </Button>
                    )}
                    {rewardSettings.status === RewardStatus.Stopped && (
                        <Button
                            variant="contained"
                            color="primary"
                            sx={{ minWidth: 200 }}
                            onClick={() => commandRewardHandler("continue")}
                            loading={isCommanding}
                            loadingIndicator="Continue…"
                        >
                            Continue Reward
                        </Button>
                    )}
                </Box>
                {rewardSettings.status !== RewardStatus.None && (
                    <Box
                        display="flex"
                        flexDirection="column"
                        gap={1}
                        sx={{ width: "100%" }}
                    >
                        <Typography variant="subtitle1">
                            Status: {rewardSettings.status}
                        </Typography>
                        <Typography variant="subtitle2" color="warning">
                            {rewardSettings.msg}
                        </Typography>
                    </Box>
                )}
            </Box>
        </Box>
    );
}
